import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowRightLeft, Check, Receipt } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";

const DONE = { rebilled: "Refacturation créée", ours: "Gardée dans cette entreprise", moved: "Transférée" };

export const MismatchPrompt = ({ company, type, item }) => {
  const qc = useQueryClient();
  const { money } = useFormat();
  const [busy, setBusy] = useState(false);
  const mm = item.mismatch;
  if (!mm) return null;
  if (mm.status !== "pending") return <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 text-[10px] font-semibold" data-testid={`mismatch-done-${item.id}`}><Check className="h-2.5 w-2.5" />Au nom de {mm.billed_to} · {DONE[mm.status]}</span>;
  const canEdit = company.my_role !== "viewer";
  const act = async (action) => {
    setBusy(true);
    try {
      await api.post(`/business/companies/${company.id}/mismatch/${type}/${item.id}`, { action });
      toast.success({ rebill: `Créance envers ${mm.billed_to} créée, avec l'action « Refacturer »`, move: `Transférée vers ${mm.billed_to}`, ours: "C'est noté, ce nom sera reconnu la prochaine fois" }[action]);
      ["biz-tx", "biz-invoices", "biz-debts", "biz-open-actions", "biz-tasks", "biz-overview", "biz-dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="w-full space-y-2 rounded-xl border border-destructive/40 bg-destructive/5 p-3 text-xs" data-testid={`mismatch-${item.id}`}>
      <p className="flex items-start gap-1.5"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
        <span>Incohérence : facture adressée à <b>{mm.billed_to}</b>{mm.siren && ` (SIREN ${mm.siren})`}, reçue chez <b>{company.name}</b> · {money(mm.amount, { maximumFractionDigits: 2 })}. Que faut-il faire ?</span></p>
      {canEdit && <div className="flex flex-wrap gap-1.5">
        <Button size="sm" className="h-7 rounded-full text-xs" disabled={busy} onClick={() => act("rebill")} data-testid={`mismatch-rebill-${item.id}`}><Receipt className="mr-1 h-3 w-3" />Refacturer à {mm.billed_to}</Button>
        {mm.company_id && <Button size="sm" variant="outline" className="h-7 rounded-full text-xs" disabled={busy} onClick={() => act("move")} data-testid={`mismatch-move-${item.id}`}><ArrowRightLeft className="mr-1 h-3 w-3" />Transférer vers {mm.billed_to}</Button>}
        <Button size="sm" variant="ghost" className="h-7 rounded-full text-xs" disabled={busy} onClick={() => act("ours")} data-testid={`mismatch-ours-${item.id}`}>C'est bien pour nous</Button>
      </div>}
    </div>
  );
};
